'use client'
import { useState, useEffect, useMemo } from 'react'

interface PriceChartProps {
  coinId: string
  initialData: [number, number][]
  color?: string
}

const TF = [
  { label: '24H', days: '1' },
  { label: '7D', days: '7' },
  { label: '30D', days: '30' },
  { label: '1Y', days: '365' },
  { label: 'ALL', days: 'max' },
]

function fmtDate(ts: number, days: string): string {
  const d = new Date(ts)
  if (days === '1')
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  if (days === '7' || days === '30')
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
}

function fmtAxis(p: number): string {
  if (p >= 10000) return '$' + (p / 1000).toFixed(0) + 'K'
  if (p >= 1000) return '$' + (p / 1000).toFixed(1) + 'K'
  if (p >= 1) return '$' + p.toFixed(2)
  if (p >= 0.01) return '$' + p.toFixed(4)
  return '$' + p.toFixed(6)
}

function fmtHover(p: number): string {
  if (p >= 1000)
    return (
      '$' +
      p.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    )
  if (p >= 1) return '$' + p.toFixed(2)
  if (p >= 0.01) return '$' + p.toFixed(4)
  return '$' + p.toFixed(8)
}

const W = 800
const H = 280
const PT = 10
const PB = 30
const DH = H - PT - PB
const GR = 6

export function PriceChart({ coinId, initialData }: PriceChartProps) {
  const [tf, setTf] = useState('7')
  const [data, setData] = useState(initialData)
  const [ld, setLd] = useState(false)
  const [hi, setHi] = useState(-1)

  useEffect(() => {
    if (tf === '7') {
      setData(initialData)
      return
    }
    let c = false
    setLd(true)
    fetch('/api/prices/chart?coin=' + coinId + '&days=' + tf)
      .then((r) => r.json())
      .then((d) => {
        if (!c && d.prices) setData(d.prices)
      })
      .catch(() => {})
      .finally(() => {
        if (!c) setLd(false)
      })
    return () => {
      c = true
    }
  }, [tf, coinId, initialData])

  const cc = useMemo(() => {
    if (!data.length)
      return {
        p: '',
        a: '',
        xl: [] as { x: number; l: string }[],
        yl: [] as { y: number; l: string }[],
        pos: true,
        mn: 0,
        rng: 1,
      }
    const pr = data.map((d) => d[1])
    const mn = Math.min(...pr)
    const mx = Math.max(...pr)
    const rng = mx - mn || 1
    const st = W / (pr.length - 1)
    const toY = (v: number) => PT + (1 - (v - mn) / rng) * DH
    const p = pr.map((v, i) => i * st + ',' + toY(v)).join(' ')
    const lx = (pr.length - 1) * st
    const ac = pr.map((v, i) => i * st + ',' + toY(v))
    const a =
      'M0,' +
      (H - PB) +
      ' L' +
      ac.join(' L') +
      ' L' +
      lx +
      ',' +
      (H - PB) +
      ' Z'
    const xl: { x: number; l: string }[] = []
    for (let i = 0; i < 5; i++) {
      const idx = Math.floor((i / 4) * (data.length - 1))
      xl.push({ x: idx * st, l: fmtDate(data[idx][0], tf) })
    }
    const yl: { y: number; l: string }[] = []
    for (let i = 0; i <= GR; i++) {
      const v = mn + (rng * (GR - i)) / GR
      yl.push({ y: toY(v), l: fmtAxis(v) })
    }
    return {
      p,
      a,
      xl,
      yl,
      pos: pr[pr.length - 1] >= pr[0],
      mn,
      rng,
    }
  }, [data, tf])

  const lc = cc.pos ? '#22c55e' : '#ef4444'

  const hv = useMemo(() => {
    if (hi < 0 || !data[hi]) return null
    const [ts, pr] = data[hi]
    const st = W / (data.length - 1)
    return {
      x: hi * st,
      y: PT + (1 - (pr - cc.mn) / cc.rng) * DH,
      pr,
      ts,
    }
  }, [hi, data, cc.mn, cc.rng])

  const gl = useMemo(() => {
    const l: number[] = []
    for (let i = 0; i <= GR; i++) l.push(PT + (i / GR) * DH)
    return l
  }, [])

  const font = "var(--font-saira), Saira, sans-serif"

  return (
    <div>
      <div className="flex gap-0 lg:gap-2">
        <div className="flex-1 relative">
          <div className="border border-zinc-800 overflow-hidden">
            {ld && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
                <div
                  className="w-5 h-5 border-2 border-zinc-600 border-t-zinc-300 animate-spin"
                  style={{ borderRadius: '50%' }}
                />
              </div>
            )}
            <svg
              viewBox={'0 0 ' + W + ' ' + H}
              className="w-full h-auto block"
              preserveAspectRatio="none"
              style={{ minHeight: 280 }}
              onMouseMove={(e) => {
                if (!data.length) return
                const r = e.currentTarget.getBoundingClientRect()
                const x = ((e.clientX - r.left) / r.width) * W
                const s = W / (data.length - 1)
                setHi(
                  Math.max(0, Math.min(data.length - 1, Math.round(x / s)))
                )
              }}
              onMouseLeave={() => setHi(-1)}
            >
              <defs>
                <linearGradient id="cg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={lc} stopOpacity="0.15" />
                  <stop offset="100%" stopColor={lc} stopOpacity="0" />
                </linearGradient>
              </defs>

              {/* Horizontal grid lines */}
              {gl.map((y, i) => (
                <line
                  key={'l' + i}
                  x1="0"
                  y1={y}
                  x2={W}
                  y2={y}
                  stroke="#1c1c20"
                  strokeWidth="0.5"
                />
              ))}

              {/* Area fill + price line */}
              {data.length > 0 && (
                <>
                  <path d={cc.a} fill="url(#cg)" />
                  <polyline
                    points={cc.p}
                    fill="none"
                    stroke={lc}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                </>
              )}

              {/* Hover crosshair */}
              {hv && (
                <>
                  <line
                    x1={hv.x}
                    y1={PT}
                    x2={hv.x}
                    y2={H - PB}
                    stroke="#52525b"
                    strokeWidth="0.5"
                    strokeDasharray="4,4"
                  />
                  <line
                    x1="0"
                    y1={hv.y}
                    x2={W}
                    y2={hv.y}
                    stroke="#52525b"
                    strokeWidth="0.5"
                    strokeDasharray="4,4"
                  />
                  <circle
                    cx={hv.x}
                    cy={hv.y}
                    r="4"
                    fill={lc}
                    stroke="black"
                    strokeWidth="2"
                  />
                </>
              )}

              {/* X labels */}
              {cc.xl.map((x, i) => (
                <text
                  key={i}
                  x={x.x}
                  y={H - 6}
                  fill="#52525b"
                  fontSize="10"
                  textAnchor={
                    i === 4 ? 'end' : i === 0 ? 'start' : 'middle'
                  }
                  fontFamily={font}
                >
                  {x.l}
                </text>
              ))}
            </svg>
          </div>

          {/* Hover tooltip — outside overflow-hidden */}
          {hv && (
            <div className="absolute top-3 right-3 text-right pointer-events-none bg-black/80 z-20 px-2 py-1">
              <div
                className="text-white text-sm font-medium tabular-nums"
                style={{ fontFamily: font }}
              >
                {fmtHover(hv.pr)}
              </div>
              <div className="text-zinc-400 text-xs">
                {new Date(hv.ts).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
            </div>
          )}
        </div>

        {/* Y-axis labels outside right — tight gap on mobile */}
        <div
          className="flex flex-col justify-between w-12 lg:w-14 flex-shrink-0"
          style={{ paddingTop: PT, paddingBottom: PB }}
        >
          {cc.yl.map((y, i) => (
            <span
              key={i}
              className="text-zinc-400 text-xs tabular-nums text-right leading-none"
              style={{ fontFamily: font, fontSize: 10 }}
            >
              {y.l}
            </span>
          ))}
        </div>
      </div>

      {/* Timeframe pills */}
      <div className="flex items-center gap-1 mt-3">
        {TF.map((t) => (
          <button
            key={t.days}
            onClick={() => setTf(t.days)}
            className={
              'px-3 py-1.5 text-xs uppercase tracking-widest transition-colors ' +
              (tf === t.days
                ? 'border border-zinc-400 text-white'
                : 'border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600')
            }
            style={{ fontFamily: font }}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  )
}